import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import DLOButton, { PlusIcon } from '../DLOButton';

// Filter Icon from Figma design
const FilterIcon = ({ color = '#005CBC' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path 
      d="M9.24288 15H12.5762V13.3333H9.24288V15ZM3.40955 5V6.66667H18.4095V5H3.40955ZM5.90955 10.8333H15.9095V9.16667H5.90955V10.8333Z" 
      fill={color} 
    />
  </Svg>
);

// Checkbox Icon
const CheckboxIcon = ({ checked = false }) => (
  <Svg width="12" height="20" viewBox="0 0 12 20" fill="none">
    <Path 
      d="M2 4.5H10C10.8284 4.5 11.5 5.17157 11.5 6V14C11.5 14.8284 10.8284 15.5 10 15.5H2C1.17157 15.5 0.5 14.8284 0.5 14V6C0.5 5.17157 1.17157 4.5 2 4.5Z" 
      stroke="#3A3A3A" 
    />
    {checked && (
      <Path 
        d="M3.5 10L5.5 12L9.5 8" 
        stroke="#005CBC" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
      />
    )}
  </Svg>
);

// Data interfaces
export interface ScheduleEmployee {
  id: string;
  name: string;
  role: string;
  shift: string;
  sunday: { fcst: number; schd: number };
  monday: { fcst: number; schd: number };
  tuesday: { fcst: number; schd: number };
  wednesday: { fcst: number; schd: number };
  thursday: { fcst: number; schd: number };
  friday: { fcst: number; schd: number };
  saturday: { fcst: number; schd: number };
  total: { fcst: number; schd: number };
  variance: number;
  isSelected?: boolean;
}

export interface DLOCreateScheduleTableProps {
  data?: ScheduleEmployee[];
  onAddEmployeeRow?: () => void;
  onSplitRole?: () => void;
  onMassUpdate?: () => void;
  onFilterByRole?: () => void;
  onFilterByDay?: () => void;
  onEmployeeSelect?: (employeeId: string, selected: boolean) => void;
  style?: ViewStyle;
}

// Sample data based on Figma design
const defaultData: ScheduleEmployee[] = [
  {
    id: '1',
    name: 'PICKER',
    role: '',
    shift: '',
    sunday: { fcst: 0, schd: 0 },
    monday: { fcst: 19.6, schd: 0 },
    tuesday: { fcst: 20.62, schd: 0 },
    wednesday: { fcst: 21.39, schd: 0 },
    thursday: { fcst: 22.22, schd: 0 },
    friday: { fcst: 27.33, schd: 0 },
    saturday: { fcst: 7.61, schd: 0 },
    total: { fcst: 118.77, schd: 0 },
    variance: 118.77,
    isSelected: false,
  },
  {
    id: '2',
    name: 'Totals (All Roles)',
    role: '',
    shift: '',
    sunday: { fcst: 0, schd: 0 },
    monday: { fcst: 19.6, schd: 0 },
    tuesday: { fcst: 20.62, schd: 0 },
    wednesday: { fcst: 21.39, schd: 0 },
    thursday: { fcst: 22.22, schd: 0 },
    friday: { fcst: 27.33, schd: 0 },
    saturday: { fcst: 7.61, schd: 0 },
    total: { fcst: 118.77, schd: 0 },
    variance: 118.77,
    isSelected: false,
  },
];

// Day filter component
interface DayFilterProps {
  day: string;
  isActive?: boolean;
  onPress?: () => void;
}

const DayFilter: React.FC<DayFilterProps> = ({ day, isActive = false, onPress }) => (
  <TouchableOpacity 
    style={[styles.dayFilter, isActive && styles.dayFilterActive]} 
    onPress={onPress}
    activeOpacity={0.7}
  >
    <Text style={[styles.dayFilterText, isActive && styles.dayFilterTextActive]}>
      {day}
    </Text>
  </TouchableOpacity>
);

// Main table component
const DLOCreateScheduleTable: React.FC<DLOCreateScheduleTableProps> = ({
  data = defaultData,
  onAddEmployeeRow,
  onSplitRole,
  onMassUpdate,
  onFilterByRole,
  onFilterByDay,
  onEmployeeSelect,
  style,
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [activeDays, setActiveDays] = useState<Set<string>>(new Set());
  const screenWidth = Dimensions.get('window').width;

  const handleEmployeeSelect = (employeeId: string) => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
    onEmployeeSelect?.(employeeId, newSelected.has(employeeId));
  };

  const handleDayFilter = (day: string) => {
    const newActiveDays = new Set(activeDays);
    if (newActiveDays.has(day)) {
      newActiveDays.delete(day);
    } else {
      newActiveDays.add(day);
    }
    setActiveDays(newActiveDays);
  };

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const isSmallScreen = screenWidth < 768;

  return (
    <View style={[styles.container, style]}>
      {/* Action Buttons Row */}
      <View style={styles.actionsContainer}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.actionsContent}
        >
          <DLOButton
            text="Add Employee Row"
            variant="primary"
            size="medium"
            iconLeading={<PlusIcon />}
            onPress={onAddEmployeeRow}
          />
          <DLOButton
            text="Split Role"
            variant="secondary"
            size="medium"
            onPress={onSplitRole}
          />
          <DLOButton
            text="Mass Update"
            variant="secondary"
            size="medium"
            onPress={onMassUpdate}
          />
          <DLOButton
            text="Filter by Role"
            variant="secondary"
            size="medium"
            iconLeading={<FilterIcon />}
            onPress={onFilterByRole}
          />
          <DLOButton
            text="Filter by Day"
            variant="secondary"
            size="medium"
            iconLeading={<FilterIcon />}
            onPress={onFilterByDay}
          />
        </ScrollView>
      </View>

      {/* Table Container */}
      <View style={styles.tableContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={true}>
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              {/* First row - Main headers with Variance */}
              <View style={styles.headerMainRow}>
                <View style={styles.leftHeaderSection}></View>
                
                {/* Days header section */}
                <View style={styles.daysHeaderSection}>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>SUNDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>MONDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>TUESDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>WEDNESDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>THURSDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>FRIDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>SATURDAY</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                  <View style={styles.dayHeaderColumn}>
                    <Text style={styles.dayHeaderTitle}>TOTAL</Text>
                    <View style={styles.daySubHeaders}>
                      <Text style={styles.daySubHeader}>RCMND</Text>
                      <Text style={styles.daySubHeader}>SCHD</Text>
                    </View>
                  </View>
                </View>

                {/* Variance column */}
                <View style={styles.varianceHeaderColumn}>
                  <Text style={styles.varianceHeaderTitle}>VARIANCE</Text>
                </View>
              </View>
            </View>

            {/* Sub-header row with column labels and day filters */}
            <View style={styles.subHeaderRow}>
              {/* Checkbox column */}
              <View style={styles.checkboxColumn}>
                <CheckboxIcon checked={false} />
              </View>
              <View style={styles.columnDivider} />
              
              {/* Employee column */}
              <View style={styles.employeeColumn}>
                <Text style={styles.subHeaderText}>Employee</Text>
              </View>
              <View style={styles.columnDivider} />
              
              {/* Role column */}
              <View style={styles.roleColumn}>
                <Text style={styles.subHeaderText}>Role</Text>
              </View>
              <View style={styles.columnDivider} />
              
              {/* Shift column */}
              <View style={styles.shiftColumn}>
                <Text style={styles.subHeaderText}>Shift</Text>
              </View>
              <View style={styles.columnDivider} />

              {/* Day filter columns */}
              {days.map((day, index) => (
                <React.Fragment key={day}>
                  <View style={styles.dayFilterColumn}>
                    <DayFilter 
                      day={day}
                      isActive={activeDays.has(day)}
                      onPress={() => handleDayFilter(day)}
                    />
                  </View>
                  {index < days.length - 1 && <View style={styles.columnDivider} />}
                </React.Fragment>
              ))}
              <View style={styles.columnDivider} />
              
              {/* Total Hours Sched column */}
              <View style={styles.totalHoursColumn}>
                <Text style={styles.subHeaderText}>Total Hours Sched</Text>
              </View>
            </View>

            {/* Data Rows */}
            {data.map((employee, index) => (
              <View key={employee.id} style={styles.dataRow}>
                {/* Checkbox */}
                <TouchableOpacity 
                  style={styles.checkboxColumn}
                  onPress={() => handleEmployeeSelect(employee.id)}
                  activeOpacity={0.7}
                >
                  <CheckboxIcon checked={selectedEmployees.has(employee.id)} />
                </TouchableOpacity>
                <View style={styles.columnDivider} />
                
                {/* Employee name */}
                <View style={styles.employeeColumn}>
                  <Text style={styles.dataText}>{employee.name}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Role */}
                <View style={styles.roleColumn}>
                  <Text style={styles.dataText}>{employee.role}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Shift */}
                <View style={styles.shiftColumn}>
                  <Text style={styles.dataText}>{employee.shift}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Sunday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.sunday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.sunday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Monday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.monday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.monday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Tuesday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.tuesday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.tuesday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Wednesday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.wednesday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.wednesday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Thursday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.thursday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.thursday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Friday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.friday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.friday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Saturday */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.saturday.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.saturday.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Total */}
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.total.fcst}</Text>
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.dayDataColumn}>
                  <Text style={styles.dataText}>{employee.total.schd}</Text>
                </View>
                <View style={styles.columnDivider} />
                
                {/* Variance */}
                <View style={styles.varianceColumn}>
                  <Text style={styles.varianceText}>{employee.variance}</Text>
                </View>
              </View>
            ))}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
    overflow: 'hidden',
  },
  actionsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
  },
  actionsContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  tableContainer: {
    flex: 1,
  },
  table: {
    minWidth: 1231, // Fixed width from Figma to ensure proper horizontal scrolling
  },
  tableHeader: {
    backgroundColor: '#005CBC',
  },
  headerMainRow: {
    flexDirection: 'row',
    height: 68,
  },
  leftHeaderSection: {
    width: 197, // Checkbox + Employee + Role + Shift columns
  },
  daysHeaderSection: {
    flexDirection: 'row',
    flex: 1,
  },
  dayHeaderColumn: {
    width: 116,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  dayHeaderTitle: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: 8,
  },
  daySubHeaders: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingHorizontal: 8,
  },
  daySubHeader: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
    textTransform: 'uppercase',
    textAlign: 'center',
    width: 42,
  },
  varianceHeaderColumn: {
    width: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  varianceHeaderTitle: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  subHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#EFF5FC',
    height: 48,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
  },
  checkboxColumn: {
    width: 49,
    justifyContent: 'center',
    alignItems: 'center',
  },
  employeeColumn: {
    width: 180,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  roleColumn: {
    width: 136,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  shiftColumn: {
    width: 136,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  dayFilterColumn: {
    width: 64,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  totalHoursColumn: {
    width: 146,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  dayDataColumn: {
    width: 48,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  varianceColumn: {
    width: 102,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  columnDivider: {
    width: 1,
    backgroundColor: '#DFDFDF',
    alignSelf: 'stretch',
  },
  subHeaderText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
    textAlign: 'center',
  },
  dayFilter: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
    minWidth: 64,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dayFilterActive: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  dayFilterText: {
    color: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
  },
  dayFilterTextActive: {
    color: '#FFFFFF',
  },
  dataRow: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    minHeight: 56,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
  },
  dataText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  varianceText: {
    color: '#008553',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
});

export default DLOCreateScheduleTable;
